import { useInfiniteQuery } from '@tanstack/react-query';

import { client } from '@/api/common';
import type { Reservation } from '@/api/reservations';

type Variables = {
  text?: string;
  locationIds?: number[];
  statuses?: string[];
  startDatetime?: string;
  endDatetime?: string;
  isBlock?: boolean;
  rentalType?: 'daily' | 'subscription';
  perPage?: number;
  enabled?: boolean;
};

type ReservationsPage = {
  data: Reservation[];
  meta: {
    page: number;
    totalPages: number;
    totalCount: number;
  };
};

// Utility to check if required params are defined
const areRequiredParamsDefined = (
  variables: Variables,
  requiredKeys: (keyof Variables)[]
) => requiredKeys.every((key) => variables[key] !== '');

const fetchReservations = async ({
  pageParam,
  variables,
}: {
  pageParam: number;
  variables: Variables;
}) => {
  try {
    let params: Record<string, any> = {
      page: pageParam,
      per_page: variables.perPage ?? 10,
      location_ids: variables.locationIds,
      statuses: variables.statuses,
    };

    if (variables.rentalType) {
      params.rental_type = variables.rentalType;
    }

    if (variables.text) {
      params.text = variables.text;
    }

    if (variables.startDatetime) {
      params.start_datetime = variables.startDatetime;
    }

    if (variables.endDatetime) {
      params.end_datetime = variables.endDatetime;
    }

    if (variables.isBlock) {
      params.is_block = variables.isBlock;
    }

    const response = await client.get('v2/internal/partners/reservations', {
      params,
    });

    return response.data;
  } catch (error) {
    console.error('fetchReservations error:', error);
    throw error;
  }
};

export const useReservations = (variables: Variables) => {
  const requiredKeys: (keyof Variables)[] = [];
  // pull out the enabled flag from the variables object as not to include it in the query key
  const { enabled = true, ...queryVariables } = variables;

  const shouldFetch =
    enabled && areRequiredParamsDefined(queryVariables, requiredKeys);

  return useInfiniteQuery({
    queryKey: ['internal/partners/reservations', queryVariables],
    queryFn: ({ pageParam = 1 }) =>
      fetchReservations({
        pageParam,
        variables: queryVariables,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ReservationsPage) => {
      if (lastPage.meta.page >= lastPage.meta.totalPages) {
        return undefined;
      }
      return lastPage.meta.page + 1;
    },
    enabled: shouldFetch,
  });
};
